import imageCompression from 'browser-image-compression';

// Lazy-loaded: the WASM HEIC decoder is large (~300KB gzipped) and should
// only cost bytes for the users who actually upload a HEIC file.
const loadHeic2any = () => import('heic2any').then((mod) => mod.default);

export const PROFILE_TARGET = {
  maxWidth: 500,
  maxHeight: 500,
  maxSizeMB: 0.08,
  label: 'profile photo',
  cropAspect: 1,
  cropShape: 'round',
};

export const COVER_TARGET = {
  maxWidth: 1600,
  maxHeight: 500,
  maxSizeMB: 0.2,
  label: 'cover photo',
  cropAspect: 3.2,
  cropShape: 'rect',
};

export const MAX_INPUT_BYTES = 20 * 1024 * 1024;

const ALLOWED_TYPES_MESSAGE =
  'Please upload a JPG, PNG, WebP, or HEIC/HEIF image.';

const HEIC_BRANDS = [
  'heic',
  'heix',
  'hevc',
  'hevx',
  'heim',
  'heis',
  'hevm',
  'hevs',
  'mif1',
  'msf1',
];

const matchesBytes = (bytes, offset, expected) =>
  expected.every((b, i) => bytes[offset + i] === b);

const readHeaderBytes = async (file, length = 32) => {
  const buf = await file.slice(0, length).arrayBuffer();
  return new Uint8Array(buf);
};

// Sniffs real file content (magic numbers), not the filename/extension or
// the browser-supplied file.type, which can both be spoofed or wrong.
const detectImageType = async (file) => {
  const bytes = await readHeaderBytes(file, 32);

  if (matchesBytes(bytes, 0, [0xff, 0xd8, 0xff])) return 'jpeg';

  if (
    matchesBytes(bytes, 0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])
  )
    return 'png';

  if (
    matchesBytes(bytes, 0, [0x52, 0x49, 0x46, 0x46]) && // 'RIFF'
    matchesBytes(bytes, 8, [0x57, 0x45, 0x42, 0x50]) // 'WEBP'
  )
    return 'webp';

  // ISO-BMFF 'ftyp' box — HEIC/HEIF container, brand at bytes 8-11
  if (matchesBytes(bytes, 4, [0x66, 0x74, 0x79, 0x70])) {
    const brand = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]);
    if (HEIC_BRANDS.includes(brand)) return 'heic';
  }

  return null;
};

let webpSupportPromise;
const supportsWebPEncoding = () => {
  if (!webpSupportPromise) {
    webpSupportPromise = new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      if (!canvas.toBlob) {
        resolve(false);
        return;
      }
      canvas.toBlob(
        (blob) => resolve(!!blob && blob.type === 'image/webp'),
        'image/webp'
      );
    });
  }
  return webpSupportPromise;
};

const loadDrawable = async (file) => {
  if (window.createImageBitmap) {
    try {
      return await createImageBitmap(file);
    } catch (e) {
      // fall through to <img> based decode below
    }
  }
  const dataUrl = await imageCompression.getDataUrlFromFile(file);
  const img = new Image();
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = dataUrl;
  });
  return img;
};

// Fits {width, height} within {maxWidth, maxHeight} preserving aspect
// ratio; never upscales, never distorts.
const fitWithinBox = (width, height, maxWidth, maxHeight) => {
  const scale = Math.min(1, maxWidth / width, maxHeight / height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('canvas.toBlob failed'))),
      type,
      quality
    );
  });

/**
 * Validates a picked file and, for HEIC/HEIF input, decodes it to a
 * browser-displayable JPEG. Returns a Blob/File safe to hand to an <img>
 * (e.g. for a crop UI) or to draw into a canvas. Throws with a
 * user-facing message on validation failure (bad format, oversized file)
 * — those should block the upload.
 */
export const validateAndDecodeImage = async (file) => {
  if (!file) throw new Error('No file selected.');

  if (file.size > MAX_INPUT_BYTES) {
    throw new Error(
      'That file is too large — please choose an image under 20MB.'
    );
  }

  const detectedType = await detectImageType(file);
  if (!detectedType) {
    throw new Error(ALLOWED_TYPES_MESSAGE);
  }

  if (detectedType === 'heic') {
    const heic2any = await loadHeic2any();
    const converted = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.9,
    });
    const convertedBlob = Array.isArray(converted) ? converted[0] : converted;
    return new File([convertedBlob], 'converted.jpg', { type: 'image/jpeg' });
  }

  return file;
};

/**
 * Crops the given pixel rectangle out of `sourceBlob` (as produced by
 * validateAndDecodeImage) and resizes it to fit `target`'s box, in a
 * single canvas draw — crop and resize happen together so nothing gets
 * compressed twice and no pixels outside the crop are ever touched by the
 * encoder. Compresses the result to WebP (JPEG fallback) targeting
 * target.maxSizeMB. Any failure past this point falls back to uploading
 * `sourceBlob` unmodified rather than blocking the user.
 */
export const cropAndCompress = async (sourceBlob, croppedAreaPixels, target) => {
  try {
    const drawable = await loadDrawable(sourceBlob);
    const { width: destWidth, height: destHeight } = fitWithinBox(
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      target.maxWidth,
      target.maxHeight
    );

    const canvas = document.createElement('canvas');
    canvas.width = destWidth;
    canvas.height = destHeight;
    canvas.getContext('2d').drawImage(
      drawable,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      destWidth,
      destHeight
    );

    const webpSupported = await supportsWebPEncoding();
    const outputType = webpSupported ? 'image/webp' : 'image/jpeg';
    const extension = webpSupported ? 'webp' : 'jpg';

    const initialBlob = await canvasToBlob(canvas, outputType, 0.8);
    const croppedFile = new File([initialBlob], `image.${extension}`, {
      type: outputType,
    });

    return await imageCompression(croppedFile, {
      maxSizeMB: target.maxSizeMB,
      initialQuality: 0.8,
      fileType: outputType,
      useWebWorker: true,
      maxIteration: 10,
    });
  } catch (err) {
    console.warn(
      `Crop/compression failed for ${target.label}, uploading original file instead.`,
      err
    );
    return sourceBlob;
  }
};
